// components/QcApproval.js - the "QC approved" card on the order screen.
// A Supervisor (role level >= 80) approves a finished set; the server passes that on to Forge.
// The confirmation is a dialog of our own: window.confirm is not translated and looks like a
// browser warning on the tablets.
import React, { useState } from 'react';
import { useLang } from '../contexts/LanguageContext';
import api from '../utils/api';
import {
  qcView, qcErrorFrom, approverName, formatQcTime, rememberQcDone, CORE_STEP_COUNT,
} from '../utils/qcApproval';

const LOCALES = { en: 'en-GB', pl: 'pl-PL' };

function ConfirmSheet({ order, busy, onConfirm, onCancel }) {
  const { t } = useLang();
  return (
    <div className="overlay" onClick={busy ? undefined : onCancel}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="qc-confirm-title"
        onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <h3 id="qc-confirm-title" style={{ marginBottom: 12 }}>{t('qc.confirm.title')}</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
          {t('qc.confirm.message').replace('{order}', order.order_number).replace('{product}', order.product_name)}
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary btn-full" autoFocus disabled={busy} onClick={onCancel}>
            {t('qc.confirm.no')}
          </button>
          <button className="btn btn-success btn-full" disabled={busy} onClick={onConfirm}>
            {busy ? t('qc.saving') : t('qc.confirm.yes')}
          </button>
        </div>
      </div>
    </div>
  );
}

function stepLabel(step, t) {
  return step.name ?? t(`qc.step.${step.index}`, `#${step.index + 1}`);
}

// `order` and `photos` come from the order answer; `onApproved` receives the fields to lay over
// the order once the server has said 200.
export default function QcApproval({ user, order, photos, onApproved }) {
  const { t, lang } = useLang();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null); // { key, missing? }

  const view = qcView({ user, order, photos });
  if (view.mode === 'hidden') return null;

  if (view.mode === 'done') {
    const time = formatQcTime(order.qc_done_at, LOCALES[lang]);
    const name = approverName(order.qc_done_by, user);
    const text = name
      ? t('qc.done').replace('{time}', time).replace('{name}', name)
      : t('qc.done.noname').replace('{time}', time);
    return (
      <div className="card" role="status" data-testid="qc-done"
        style={{ marginBottom: 16, borderColor: 'var(--accent-success)', color: 'var(--accent-success)', fontWeight: 600 }}>
        ✓ {text}
      </div>
    );
  }

  const approve = async () => {
    setBusy(true);
    try {
      const { data: answer } = await api.post(`/api/orders/${order.id}/qc-done`);
      const done = {
        qc_status: 'done',
        qc_done_at: answer.qc_done_at,
        qc_done_by: approverName(answer.qc_done_by, user),
      };
      rememberQcDone(order.id, done);
      setError(null);
      onApproved(done);
    } catch (err) {
      setError(qcErrorFrom(err));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const missingList = (steps) => steps.map((s) => stepLabel(s, t)).join(', ');

  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="qc-card">
      <div className="form-label" style={{ marginBottom: 8 }}>{t('qc.title')}</div>
      <button className="btn btn-success btn-full" disabled={!view.enabled || busy}
        onClick={() => { setError(null); setConfirming(true); }}>
        ✓ {t('qc.button')}
      </button>
      {view.enabled ? (
        <div style={{ marginTop: 8, fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
          {t('qc.hint.ready').replace('{count}', CORE_STEP_COUNT)}
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: '0.8125rem', color: 'var(--accent-warning)' }} data-testid="qc-blocked">
          {t('qc.blocked').replace('{steps}', missingList(view.missing.map((index) => ({ index }))))}
        </div>
      )}
      {error && (
        <div className="warning-banner" role="alert" style={{ marginTop: 10 }} data-testid="qc-error">
          <span>⚠</span>
          <span>{t(error.key).replace('{steps}', missingList(error.missing || []))}</span>
        </div>
      )}
      {confirming && <ConfirmSheet order={order} busy={busy} onConfirm={approve} onCancel={() => setConfirming(false)} />}
    </div>
  );
}
